import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AuthService } from '../auth.service';
import { SigninData, SigninLearningCenterData, OAuthRedirectData } from '../auth-types';
import { CookieService } from 'ngx-cookie-service';
import { SystemGlobalsService } from '../../system-globals.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import * as Bowser from 'bowser';

@Component({
  selector: 'signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit {

  public username: string = '';
  public password: string = '';
  public networkId: number;

  public resetMessage: string = '';
  public hasResetError: boolean = false;
  public errorMessage: string = 'no error';
  public separatedLoginRedirectUrl: string = '';
  /** Origin only (e.g. https://host) for display; link still uses full sign-in URL. */
  public separatedLoginSiteUrl: string = '';
  public hasError: boolean = false;
  public networks: SigninLearningCenterData[] = [];
  public filteredNetworks: SigninLearningCenterData[] = [];
  public chooseLc: boolean = false;
  public rememberUsername: boolean = false;
  public autoFocusUsername: boolean = true;
  public show: boolean = true;

  public isBusySigningIn: boolean = false;
  public isBusyResettingPassword: boolean = false;
  private isTracking: boolean = false;
  public isSwitchingLc: boolean = false;

  public isInIframe: boolean = false;
  public system: string = 'ascent';

  public showPassword: boolean = false;
  public showForgotPassword: boolean = false;
  public showStoreMessage: boolean = false;

  public lcOwnerCookie: string = "";
  public showRedirectMessage: boolean = false;
  public redirecting: boolean = false;
  public redirectCountdown: number = 10;

  oAuthRequiredParams: string[] = ['oauthData'];
  oAuthData: OAuthRedirectData;
  public oAuthDataValid: boolean = false;
  public hasOAuthData: boolean = false;

  public isIe11: boolean = false;

  searchText$: Subject<string> = new Subject<string>();
  searchTerm: string = '';
  placeholderText: string = '';

  sendUserIdOnRedirect: boolean;
  redirect: string;

  @ViewChild('signInForm') signInForm: ElementRef;

  constructor(private authService: AuthService, private cookieService: CookieService, public globals: SystemGlobalsService, private route: ActivatedRoute) {
    this.searchText$.pipe(debounceTime(200), distinctUntilChanged()).subscribe(searchTerm => {
      this.searchTerm = searchTerm;

      let term = searchTerm.toLocaleLowerCase();

      if (term.length) this.filteredNetworks = this.networks.filter(network => network.name.toLocaleLowerCase().includes(term));
      else this.filteredNetworks = this.networks;
    });

    this.populateOAuthData();
  }

  ngOnInit() {
    this.isInIframe = this.inIframe();

    this.sendUserIdOnRedirect = this.globals.getUrlParamByName('sendUserIdOnRedirect') == 'true';
    this.redirect = this.globals.getUrlParamByName('redirect');
    this.isSwitchingLc = this.globals.getUrlParamByName('switch') != null;
    
    if (this.globals.constants.authenticated && this.globals.constants._g_userRole == 5 && this.oAuthData?.oauthData) {
      window.top.location.href = '/main/api/authorize/' + encodeURIComponent(this.oAuthData.oauthData);

      this.logEvent('Oauth redirect', JSON.stringify( this.oAuthData.oauthData));
      return;
    }

    if (this.redirect) this.redirect = atob(this.redirect);

    if (this.globals.constants.authenticated) {
      this.show = false;

      if (this.isInIframe) window.top.location.href = '/';
      else {
        if (this.redirect) {
          if (this.sendUserIdOnRedirect) this.redirect = this.globals.updateQueryString('systemUserId', this.globals.constants._g_userID, this.redirect);
          window.top.location.href = this.redirect;
        }
        else window.location.href = '/';
      }
    }

    if (window.location.hostname.split('.')[1] == 'allantra') this.system = 'avanti';

    let username = this.cookieService.get('username');

    if (username) {
      this.username = username;
      this.rememberUsername = true;
      this.autoFocusUsername = false;
    }

    if (this.isSwitchingLc)
    {
      this.username = this.globals.constants.signinUsername;
      this.password = this.globals.constants.signinPassword;

      this.chooseLc = true;
      
      this.setupSwitchLC();
    }
    else
    {
      if (this.globals.constants.signinChooseLC) {
        this.username = this.globals.constants.signinUsername;
        this.password = this.globals.constants.signinPassword;

        this.chooseLc = true;
        this.signIn(true);
      }
    }

    if (this.globals.getUrlParamByName('info') == 'from-store') this.showStoreMessage = true;

    if (this.globals.getUrlParamByName('reset')) setTimeout(() => {
      this.showForgotPassword = true;
      this.username = this.globals.getUrlParamByName('reset');
    }, 500);

    this.isIe11 = !!window['MSInputMethodContext'] && !!document['documentMode'];

    this.lcOwnerCookie = this.getCookie('lc_owner');
    if (this.lcOwnerCookie === '3004') {
      this.separatedLoginSiteUrl = 'https://aircrewacademy.aerostudies.com';
      this.separatedLoginRedirectUrl = 'https://aircrewacademy.aerostudies.com/main/auth/signin';
      this.hasError = true;
      this.redirecting = true;
      this.errorMessage = 'Your account has been moved to a different URL';
      const redirectTimer = window.setInterval(() => {
        this.redirectCountdown -= 1;
        if (this.redirectCountdown <= 0) { window.clearInterval(redirectTimer); window.location.href = this.separatedLoginRedirectUrl; }
      }, 1000);
    }
  }

  setupSwitchLC()
  {
    let lcData = [];
    if (this.globals.constants.authorizedUsers.length) lcData = JSON.parse(this.globals.constants.authorizedUsers);

    lcData = lcData.sort((a, b) => a.name.localeCompare(b.name));

    this.networks = lcData;
    this.filteredNetworks = this.networks;
    this.isBusySigningIn = false;
  }

  populateOAuthData() {
    this.oAuthData = <OAuthRedirectData>this.route.snapshot.queryParams;

    if (Object.keys(this.oAuthData).length == 0 || this.oAuthData.oauthData == undefined) {
      this.oAuthData = undefined;
      return;
    }
    else this.hasOAuthData = true;

    let hasRequirements = this.oAuthRequiredParams.every(
      key => this.oAuthData[key] !== undefined
    );

    if (hasRequirements) this.oAuthDataValid = true;
  }

  inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  ngAfterViewInit() {
    window.document.body.style.backgroundColor = 'transparent';
    if (this.isInIframe) setTimeout(() => window.document.body.style.backgroundColor = 'transparent', 1000);
  }

  clearSearch() {
    this.placeholderText = '';
    this.searchText$.next('');
  }

  canSignIn(): boolean {
    return this.username.length > 0 && this.password.length > 0 && !this.isBusySigningIn;
  }

  canResetPassword(): boolean {
    return this.username.length > 0;
  }

  updateUsernameCookie() {
    if (this.rememberUsername) this.cookieService.set('username', this.username, 365, '/');
    else this.cookieService.delete('username');
  }

  toggleForgotPassword() {
    this.resetMessage = '';
    this.showForgotPassword = !this.showForgotPassword;
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    return true;
  }

  logEvent(event: string, data: string) {
    if (!this.isTracking) return;

    // this.authService.logSignInEvent({
    //   username: this.username,
    //   event: event,
    //   data: data
    // }).subscribe();
  }

  trackSignIn() {
    // if (this.username.includes('aircrewacademy') && !this.isTracking) {
    //   this.isTracking = true;

    //   (function() {
    //     window['__insp'] = window['__insp'] || [];
    //     window['__insp'].push(['wid', 1427344387]);
    //     var ldinsp = function(){
    //     if(typeof window['__inspld'] != "undefined") return; window['__inspld'] = 1; var insp = document.createElement('script'); insp.type = 'text/javascript'; insp.async = true; insp.id = "inspsync"; insp.src = ('https:' == document.location.protocol ? 'https' : 'http') + '://cdn.inspectlet.com/inspectlet.js?wid=1427344387&r=' + Math.floor(new Date().getTime()/3600000); var x = document.getElementsByTagName('script')[0]; x.parentNode.insertBefore(insp, x); };
    //     setTimeout(ldinsp, 0);
    //   })();

    //   setTimeout(function() {
    //     window['__insp'].push(['identify', this.username]);
    //     window['__insp'].push(['tagSession', {
    //       browser: Bowser.parse(window.navigator.userAgent),
    //     }]);
    //   }, 1000);

    //   this.logEvent('Started tracking', JSON.stringify(Bowser.parse(window.navigator.userAgent)));
    // }
  }

  signIn(skipSaveSession: boolean = false) {
    this.trackSignIn();
    this.updateUsernameCookie();

    this.hasError = false;
    if (!this.canSignIn()) return;

    let signinData: SigninData = {
      username: this.username,
      password: this.password,
      networkId: this.networkId,
      skipSaveSession: skipSaveSession,
      oAuth: this.oAuthData?.oauthData,
      login_ui_type: 'signin' // Indicate this is from the old signin UI
    };

    this.logEvent('Clicked sign in', JSON.stringify(signinData));

    this.isBusySigningIn = true;

    this.authService.signin(signinData).subscribe(
      data => {
        if (data.hasError) {
          this.hasError = true;
          this.errorMessage = data.errorMessage;
          this.separatedLoginRedirectUrl = data.separatedLoginRedirectUrl || '';
          this.separatedLoginSiteUrl = this.toSiteUrlOnly(this.separatedLoginRedirectUrl);
          this.password = '';
          this.isBusySigningIn = false;
          this.logEvent('Sign in validation error', JSON.stringify(data));
          if (this.separatedLoginSiteUrl && this.separatedLoginSiteUrl !== '') {
            this.redirecting = true;
            const redirectTimer = window.setInterval(() => {
              this.redirectCountdown -= 1;
              if (this.redirectCountdown <= 0) { window.clearInterval(redirectTimer); window.location.href = this.separatedLoginRedirectUrl; }
            }, 1000);
          }
        }
        else {
          this.hasError = false;
          this.separatedLoginRedirectUrl = '';
          this.separatedLoginSiteUrl = '';

          if (data.chooseLC) {
            this.logEvent('Choose LC', JSON.stringify(data));

            // If in iframe, expand iframe for properly choosing LC
            if (this.isInIframe) {
              this.logEvent('Break from iframe', JSON.stringify(data));
              this.signInForm.nativeElement['submit']();
              return;
            }

            data.lcData.sort((a, b) => a.name.localeCompare(b.name));

            this.networks = data.lcData;
            this.filteredNetworks = this.networks;
            this.chooseLc = data.chooseLC;
            this.isBusySigningIn = false;

            this.logEvent('Displaying LC choices', JSON.stringify(data));
          }
          else {
            if (data.oAuthRedirect && data.oAuthRedirect === true) {
              window.top.location.href = '/main/api/authorize/' + encodeURIComponent(this.oAuthData.oauthData);

              this.logEvent('Oauth redirect', JSON.stringify(data));
            }
            else {
              if (this.redirect) {
                if (this.sendUserIdOnRedirect) this.redirect = this.globals.updateQueryString('systemUserId', data.userId, this.redirect);
                window.top.location.href = this.redirect;
              }
              else {
                this.logEvent('Home redirect', JSON.stringify(data));
                window.top.location.href = '/home/index';
              }
            }
          }
        }
      },
      error => {
        this.logEvent('Sign in error', JSON.stringify(error));

        this.password = '';
        this.isBusySigningIn = false;
      }
    );
  }

  resetPassword() {
    if (!this.canResetPassword()) return;

    this.resetMessage = '';
    this.hasResetError = false;

    this.isBusyResettingPassword = true;

    this.authService.resetPassword(this.username).subscribe(
      data => {
        this.isBusyResettingPassword = false;
        this.resetMessage = '<b>A password reset email will be sent shortly.</b><ul><li>WAIT for it — do not click \'reset\' again.</li><li>LOOK in your spam/junk folder.</li></ul>';

        if (data) {
          if (data.hasError) {
            this.resetMessage = data.errorMessage;
            this.hasResetError = true;
          }
        }
      },
      () => {
        this.isBusyResettingPassword = false;
        this.resetMessage = '<b>A password reset email will be sent to you shortly if this was a valid email address.</b>';
      }
    )
  }

  /**
   * Strip path/query so we only show the site URL in the UI (not /main/auth/signin).
   */
  private toSiteUrlOnly(fullUrl: string): string {
    if (!fullUrl) {
      return '';
    }
    try {
      return new URL(fullUrl).origin;
    } catch {
      return fullUrl.replace(/\/main\/auth\/signin\/?.*$/i, '').replace(/\/+$/, '') || fullUrl;
    }
  }

  selectNetwork(networkId: number) {
    this.networkId = networkId;

    this.logEvent('Selected network', JSON.stringify(networkId));

    this.signIn();
  }

  getCookie(name: string): string {
    if (name === 'lc_owner') {
      return this.globals.constants.lcOwnerCookie || '';
    }

    return '';
  }
}
